import { useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { CallGroup as SchemaCallGroup } from '@weave/schema-gen-ts/dist/schemas/phone/callgroup/v1/callgroup.pb';
import { CallGroup as DevicesCallGroup } from '@weave/schema-gen-ts/dist/schemas/phone/devices/v2/devices.pb';
import { SoftphoneApi } from '@frontend/api-softphone';
import { UsersQueries } from '@frontend/api-users';
import { useTranslation } from '@frontend/i18n';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useQuery } from '@frontend/react-query-helpers';
import { theme } from '@frontend/theme';
import { Heading, MultiStep, MultiStepModalProps, ContentLoader } from '@frontend/design-system';
import { ConfirmSoftphoneModalExit } from './confirm-exit';
import { useCreateSoftphoneMutation } from './hooks/use-create-softphone';
import { useCreateSoftphoneForm } from './hooks/use-create-softphone-form';
import { FirstStep } from './steps/first-step';
import { FourthStep } from './steps/fourth-step';
import { LocationSetupStep } from './steps/location-setup';
import { SecondStep } from './steps/second-step';
import { SuccessPage } from './steps/success-page';
import { ThirdStep } from './steps/third-step';

export type CreateSoftphonePropTypes = {
  getFieldProps: ReturnType<typeof useCreateSoftphoneForm>['getFieldProps'];
  values?: ReturnType<typeof useCreateSoftphoneForm>['values'];
  locationIds: string[];
  tenantId?: string;
};

export type CallGroupDevicesListType = SchemaCallGroup & {
  locationId: string;
};

export const CreateSoftphoneStepper = (modalControls: MultiStepModalProps) => {
  const [lastActiveStepBeforeExit, setLastActiveStepBeforeExit] = useState('');
  const { settingsTenantLocation, globalAvailableLocationIds } = usePhoneSettingsShallowStore(
    'settingsTenantLocation',
    'globalAvailableLocationIds'
  );
  const [errorMessage, setErrorMessage] = useState('');
  const { getFieldProps, values } = useCreateSoftphoneForm({});
  const isMultiLocation = settingsTenantLocation?.locationType === 'unify';
  const initialCallGroupSelections = useRef<Record<string, boolean>>({});
  const [selectedCallGroups, setSelectedCallGroups] = useState<CallGroupDevicesListType[]>([]);
  const tenantId = settingsTenantLocation?.phoneTenantId;
  const { t } = useTranslation('phone', { keyPrefix: 'create-new-softphone' });
  const callGroupsMapped: DevicesCallGroup[] = selectedCallGroups.map((group) => ({ id: group.id, name: group.name }));
  const locationIds = globalAvailableLocationIds ?? [];
  const { data } = UsersQueries.useGetUsersInLocations(locationIds);
  const { data: userSoftphones } = useQuery({
    queryKey: [locationIds, 'user-softphones'],
    queryFn: () => SoftphoneApi.list(),
    meta: {
      error: {
        message: 'CreateSoftphoneStepper:userSoftphonesQuery | Failed to fetch user softphones',
        data: { locationIds },
      },
    },
  });
  const users = useMemo(() => {
    return data?.users ?? [];
  }, [data?.users]);

  const usersWithoutSoftphones = useMemo(
    () =>
      users
        .filter((user) => !userSoftphones?.some((softphone) => softphone.user_id === user.userId))
        .sort((a, b) => (a.firstName && b.firstName ? a.firstName.localeCompare(b.firstName) : 0)),
    [users, userSoftphones]
  );

  const { onSubmit, isLoading, isFormSubmitted } = useCreateSoftphoneMutation({
    tenantId,
    locationIds: globalAvailableLocationIds,
  });

  const props = {
    getFieldProps,
    values,
    locationIds,
    isMultiLocation,
    initialCallGroupSelections,
    setSelectedCallGroups,
    tenantId: settingsTenantLocation?.phoneTenantId,
  };

  const isAddingNewAddress = getFieldProps('assignedAddressID').value === 'addNewAddress';
  const e911HeaderName = isAddingNewAddress ? 'Add a New E911 Address' : 'Assign an E911 Address';
  const showExitScreen = modalControls.activeStep === '0';

  const isOptionalStepComplete = modalControls.activeStepIndex <= modalControls.activeParentStepIndex;

  const isStep1 = modalControls.activeStep === '1';
  const isStep2 = modalControls.activeStep === '2';
  const isStep2Valid = !!values?.deviceName && !!values.internalIDName;
  const clickDisabled = isFormSubmitted || isStep1 || (isStep2 && !isStep2Valid);

  return (
    <MultiStep.Modal
      minWidth={800}
      {...modalControls}
      disableCloseOnEscape
      disableCloseOnOverlayClick
      stepper={{
        show: showExitScreen ? false : true,
        setShow: () => {},
        display: 'full',
        setDisplay: () => {},
        clickDisabled: !!clickDisabled,
        setClickDisabled: () => {},
        scrollDisabled: false,
        setScrollDisabled: () => {},
      }}
      closeModal={() => {}}
      onCancel={() => {
        // should reset on success page
        if (modalControls.activeStep === '4.a') {
          modalControls.setActiveStep('1');
          return modalControls.closeModal();
        }
        // First step with no data already added should not show confirm exit screen
        if (!values.userID && !values.extension) {
          return modalControls.closeModal();
        }
        if (showExitScreen) {
          modalControls.setActiveStep('1');
          modalControls.closeModal();
        } else {
          // store active step the screen was on before it went to the confirm exit screen
          setLastActiveStepBeforeExit(modalControls.activeStep);
          modalControls.setActiveStep('0');
        }
      }}
      css={modalContainerStyles(showExitScreen)}
    >
      <ContentLoader show={isLoading} />
      {showExitScreen && (
        <MultiStep.Step
          id='0'
          label=''
          header={{
            title: () => <Heading level={2}>{t('Quit Softphone Setup')}</Heading>,
          }}
          footer={{
            primary: {
              destructive: true,
              label: t('Quit Setup'),
              onClick: () => {
                modalControls.closeModal();
                modalControls.setActiveStep('1');
              },
            },
            secondary: {
              label: t('Cancel'),
              onClick: () => {
                modalControls.goToStep({ orStep: lastActiveStepBeforeExit });
              },
            },
          }}
        >
          <ConfirmSoftphoneModalExit />
        </MultiStep.Step>
      )}
      <MultiStep.Step
        header={{
          title: () => <Heading level={2}>{t('Select User and Extension')}</Heading>,
        }}
        footer={{
          primary: {
            label: 'Continue',
            disabled: !getFieldProps('userID').value || !getFieldProps('extension').value || !!errorMessage,
            onClick: () => {
              if (!!getFieldProps('deviceName').value && !!getFieldProps('internalIDName').value) return;
              const extension = getFieldProps('extension').value;
              const userFound = users?.find((user) => user.userId === values?.userID);
              getFieldProps('firstName').onChange({ name: 'firstName', value: `${userFound?.firstName}` });
              getFieldProps('deviceName').onChange({
                name: 'deviceName',
                value: `${extension + ' ' + userFound?.firstName}'s Softphone`,
              });
              getFieldProps('internalIDName').onChange({
                name: 'internalIDName',
                value: `${extension + ' ' + userFound?.firstName}'s Softphone`,
              });
            },
          },
        }}
        id='1'
        label={t('User & Extension')}
        css={stepBodyStyles}
        isComplete={!!getFieldProps('extension').value && !!getFieldProps('userID').value}
      >
        <FirstStep
          usersWithoutSoftphones={usersWithoutSoftphones}
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
          {...props}
        />
      </MultiStep.Step>
      <MultiStep.Step
        header={{
          title: () => <Heading level={2}>{t('Set the Device Name')}</Heading>,
        }}
        footer={{
          primary: {
            label: 'Continue',
            disabled: !values?.deviceName || !values.internalIDName,
          },
          showBackButton: true,
        }}
        id='2'
        label={t('Device Name')}
        css={stepBodyStyles}
        isComplete={!!getFieldProps('userID').value && !!getFieldProps('extension').value}
      >
        <SecondStep {...props} />
      </MultiStep.Step>
      {(settingsTenantLocation?.children?.length ?? 0) > 0 && (
        <MultiStep.Step
          header={{
            title: () => <Heading level={2}>{t('Assign a Location')}</Heading>,
          }}
          components={{
            Footer: (props) => {
              return (
                <MultiStep.Step.Footer
                  {...props}
                  onClickSecondary={(props) => {
                    props.goNextStep();
                  }}
                  secondary={{
                    trackingId: 'skip-e911-address',
                    label: t('Assign Later'),
                  }}
                  primary={{
                    trackingId: 'proceed-e911-address',
                  }}
                />
              );
            },
          }}
          id='location-step'
          label={t('Location')}
          css={stepBodyStyles}
          isComplete={isOptionalStepComplete}
        >
          <LocationSetupStep {...props} />
        </MultiStep.Step>
      )}
      <MultiStep.Step
        header={{
          title: () => <Heading level={2}>{e911HeaderName}</Heading>,
        }}
        components={{
          Footer: (props) => {
            if (isAddingNewAddress) return null;
            return (
              <MultiStep.Step.Footer
                {...props}
                onClickSecondary={(props) => {
                  props.goNextStep();
                }}
                secondary={t('Assign Later')}
              />
            );
          },
        }}
        id='3'
        label={t('E911')}
        css={stepBodyStyles}
        isComplete={isOptionalStepComplete}
      >
        <ThirdStep {...props} />
      </MultiStep.Step>

      <MultiStep.Step
        header={{
          title: () => <Heading level={2}>{t('Assign to Call Groups')}</Heading>,
        }}
        components={{
          Footer: (props) => {
            return (
              <MultiStep.Step.Footer
                {...props}
                onClickPrimary={(props) => {
                  onSubmit(values, callGroupsMapped).then(() => {
                    props.goNextStep();
                    props.setComplete(true);
                  });
                }}
                onClickSecondary={(props) => {
                  onSubmit(values, callGroupsMapped).then(() => {
                    props.goNextStep();
                    props.setComplete(true);
                  });
                }}
                primary={t('Complete Setup')}
                secondary={t('Assign Later')}
              />
            );
          },
        }}
        id='4'
        label={t('Call-Groups')}
        css={stepBodyStyles}
        isComplete={isOptionalStepComplete}
      >
        <FourthStep {...props} />
      </MultiStep.Step>
      <MultiStep.Step
        parentId='4'
        header={{
          title: () => <Heading level={2}>{t('Softphone Setup is Complete!')}</Heading>,
          cancelBtnTrackingId: 'escape-softphone-modal',
        }}
        components={{
          Footer: () => null,
        }}
        id='4.a'
        label=''
      >
        <SuccessPage {...props} />
      </MultiStep.Step>
    </MultiStep.Modal>
  );
};

const modalContainerStyles = (showExitScreen: boolean) => css`
  .multi-step__body-wrapper {
    min-height: ${showExitScreen ? '0' : '350px'};
  }
`;

const stepBodyStyles = css`
  .step__body {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  }
`;

export const inputFieldStyles = css`
  width: 325px;
  margin-bottom: ${theme.spacing(1)};
`;
